import { useState, useRef } from "react";
import classnames from "classnames";
import { useDropdownFocus } from "library/hooks/dropdownFocus";
import { useEventTracking } from "react-event-tracker";
import { Chevron } from "../Chevron/Chevron";
import { CreditCard } from "../CreditCard/CreditCard";

import styles from "./product-selector.module.scss";

const NEXT_KEY_CODES = [13, 32];

const SelectItemContent = ({ account, products }) => {
  if (account) {
    const product =
      products.find((item) => item.id === account.product_id) || {};

    return (
      <CreditCard
        cardName={product.name}
        cardEndsWith={account.credit_card.linked_card_last4}
      />
    );
  }

  return <>Select an account</>;
};

function ProductSelector(props) {
  const { trackEvent } = useEventTracking();
  const selectRef = useRef(null);
  const selectOptionsRef = useRef(null);
  const [selectedAccountId, setAccount] = useState(
    props.paymentInfo && props.paymentInfo.accountId,
  );
  const [showProducts, setShowProducts] = useState(false);

  // Add listeners to close product selector when it loses focus
  useDropdownFocus(selectRef, selectOptionsRef, () => setShowProducts(false));

  // Filter out disabled cards
  const accounts = props.accountList.filter(
    (acct) => acct.links && acct.links.length,
  );

  const setSelectedProduct = (accountId, productId) => {
    setAccount(accountId);
    setShowProducts(false);
    props.onChange(productId, accountId);
    selectRef.current.focus();
  };
  const selectBoxClass = !selectedAccountId
    ? styles.selectBoxNoSelection
    : styles.selectBoxWithSelection;

  return (
    <div className={`${styles.selectBox} ${selectBoxClass}`}>
      <div className={styles.selectBoxContainer}>
        <button
          type="button"
          ref={selectRef}
          data-trackid="product-selector"
          className={classnames(
            styles.selectButton,
            selectedAccountId ? styles.selectedItem : styles.selectBoxEmptyItem,
          )}
          onClick={(e) => {
            e.target.focus(); // safari fix
            setShowProducts(!showProducts);
          }}
        >
          <SelectItemContent
            account={
              selectedAccountId
                ? accounts.find((a) => a.account_id === selectedAccountId)
                : null
            }
            products={props.products}
          />
          <div
            className={`${styles.selectBoxArrow} ${
              selectedAccountId ? styles.selectBoxArrowSelectedItem : null
            }`}
          >
            <Chevron direction={showProducts ? "up" : "down"} strokeGreyDark />
          </div>
        </button>
        <div
          style={{ display: showProducts ? "block" : "none" }}
          className={styles.selectBoxItems}
          ref={selectOptionsRef}
        >
          {accounts.map((account) => {
            if (selectedAccountId !== account.account_id) {
              return (
                <button
                  type="button"
                  key={account.account_id}
                  data-trackid="product-selector-item"
                  onMouseDown={() => {
                    const product = props.products.find(
                      (p) => p.id === account.product_id,
                    );
                    trackEvent({
                      event: {
                        category: "dropdown",
                        action: "calculator",
                        location: "Ways to pay",
                        label: product.name,
                      },
                    });
                    setSelectedProduct(account.account_id, account.product_id);
                  }}
                  onKeyDown={(e) => {
                    if (NEXT_KEY_CODES.includes(e.keyCode)) {
                      setSelectedProduct(
                        account.account_id,
                        account.product_id,
                      );
                    }
                  }}
                  className={styles.selectBoxItem}
                  tabIndex={0}
                >
                  <SelectItemContent
                    account={account}
                    products={props.products}
                  />
                </button>
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
}

export { ProductSelector };
